import React from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { formatListPrice } from "../../helpers"
import { getOriginalIfDiscounted, getRateComparison } from "../pricing"
import PanelSection from "./PanelSection"
import styles from "./BookingPanel.module.css"

// Price shown on one duration card for the selected date: the flat rate "for N guests", or the
// adult price "/ adult". The pre-adjustment price is struck through when the date is discounted.
const DurationPrice = ({ experience, duration, date }) => {
  const { t } = useTranslation()
  const priceData = duration?.price || {}
  const { rates, baseRates } = getRateComparison({ experience, priceData, date })
  const isFlatRate = priceData.pricing_type === "charge_flat_rate"
  const price = isFlatRate ? rates.flatAmount : rates.pricePerPerson
  const original = getOriginalIfDiscounted(isFlatRate ? baseRates.flatAmount : baseRates.pricePerPerson, price)
  const includedGuests = priceData.included_guests ?? priceData.max_guests

  return (
    <span className={styles.durationPrice}>
      {original != null && <span className={styles.struck}>{formatListPrice(original)}</span>}
      <span className={styles.durationAmount}>
        {formatListPrice(price)}
        {!isFlatRate && ` ${t("booking_widget.experience.per_adult")}`}
      </span>
      {isFlatRate && includedGuests > 0 && <span>{t("booking_widget.experience.for_guests", { count: includedGuests })}</span>}
    </span>
  )
}

DurationPrice.propTypes = {
  experience: PropTypes.object,
  duration: PropTypes.object.isRequired,
  date: PropTypes.string
}

const DurationSection = ({ experience, durations, selectedKey, date, onSelect, error }) => {
  const { t } = useTranslation()

  return (
    <PanelSection title={t("booking_widget.experience.select_duration")} error={error}>
      <div className={styles.durationGrid} role="radiogroup" aria-label={t("booking_widget.experience.select_duration")}>
        {durations.map((duration) => {
          const isSelected = duration.key === selectedKey
          return (
            <button
              key={duration.key}
              type="button"
              role="radio"
              aria-checked={isSelected}
              className={`${styles.durationCard} ${isSelected ? styles.durationCardSelected : ""}`}
              onClick={() => onSelect(duration)}
            >
              <span className={styles.durationLabel}>{duration.value}</span>
              <DurationPrice experience={experience} duration={duration} date={date} />
            </button>
          )
        })}
      </div>
    </PanelSection>
  )
}

DurationSection.propTypes = {
  experience: PropTypes.object,
  durations: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), value: PropTypes.string, price: PropTypes.object }))
    .isRequired,
  selectedKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  date: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  error: PropTypes.string
}

DurationSection.defaultProps = {
  experience: null,
  selectedKey: null,
  date: null,
  error: null
}

export default DurationSection
